import { useEffect, useState } from "react";

/**
 * React hook that returns whether a CSS media query currently matches.
 *
 * Usage:
 *   const isDesktop = useMediaQuery("(min-width: 768px)");
 *
 * SSR-safe: returns `false` on the server (no window). First client render
 * reads `window.matchMedia` synchronously, so the component commits with the
 * correct value — no post-mount flicker.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mql = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Sync once on mount in case the query string or environment changed.
    setMatches(mql.matches);
    mql.addEventListener("change", handleChange);

    return () => {
      mql.removeEventListener("change", handleChange);
    };
  }, [query]);

  return matches;
}
